'use client';

import CurrencyInput from '@ui/Input/masked/CurrencyInput';
import { useQuery } from '@tanstack/react-query';
import { getProductByIdNotClean } from '@util/firestore/products';
import { getTax } from '@util/firestore/taxjar';
import useDebounce from '@util/hooks/useDebounce';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { OfferDocument } from 'models/offer';
import { useEffect, useState } from 'react';
import { useOffers } from '../useOffers';

interface CounterOfferContentProps {
  offer: OfferDocument;
  onClose: () => void;
}

export default function CounterOfferContent({
  offer,
  onClose,
}: CounterOfferContentProps) {
  const [counterPrice, setCounterPrice] = useState<number>(0);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const debouncedCounterPrice = useDebounce(counterPrice, 500);
  const [tax, setTax] = useState<number>(offer.tax);
  const [loadingTax, setLoadingTax] = useState(false);
  const [taxError, setTaxError] = useState<Error | null>(null);

  const { data: product } = useQuery({
    queryKey: ['product-with-shipment', offer.product_id],
    queryFn: () => getProductByIdNotClean(offer.product_id),
    enabled: !!offer.product_id,
  });

  useEffect(() => {
    if (debouncedCounterPrice && debouncedCounterPrice !== offer.price) {
      setLoadingTax(true);
      setTaxError(null);
      getTax({
        product_id: offer.product_id,
        seller_id: offer.seller_id,
        to_address: offer.address!,
        total: debouncedCounterPrice,
        shipping: offer.shipping_cost,
      })
        .then((tax) => {
          setTax(tax.amount);
        })
        .catch((e) => {
          logError(e);
          setTaxError(e as Error);
        })
        .finally(() => {
          setLoadingTax(false);
        });
    }
  }, [
    debouncedCounterPrice,
    offer.price,
    offer.product_id,
    offer.seller_id,
    offer.address,
    offer.shipping_cost,
  ]);

  const { counterOffer } = useOffers();

  return (
    <>
      <div className="flex flex-col justify-start gap-y-8 px-12 py-8">
        <h3 className="text-[2rem] font-semibold text-zinc-900">
          Enter your counter offer price!
        </h3>

        <div className="h-[10rem] w-[45rem]">
          {!counterPrice ? (
            <span className="border-l-2 border-zinc-300 pl-8 text-[1.8rem] font-semibold text-zinc-500">
              Provide your counter offer price
            </span>
          ) : (
            <div className="flex h-full border-l-2 border-zinc-300">
              <span className="pl-8 text-[1.8rem] font-semibold text-zinc-500">
                If the buyer accepts, they will be charged this counter price (
                {formatCurrency(counterPrice)}) + {formatCurrency(tax ?? 0)} Tax
                + {formatCurrency(offer.shipping_cost)} Shipping.
              </span>
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-y-2">
          <div className="flex w-full items-center gap-x-2">
            <h3 className="text-[2rem] font-bold text-black">Counter price</h3>
            <h3 className="text-[2rem] font-bold text-brand-secondary">*</h3>
          </div>

          <div className="flex w-full items-center gap-x-4 rounded-[1.5rem] bg-brand-lightest-gray px-12 py-6">
            <span className="text-[1.8rem] font-bold text-black">
              <DollarIcon />
            </span>

            <CurrencyInput
              maskOptions={{
                decimalLimit: 2,
                integerLimit: 7,
                decimalSymbol: '.',
                allowDecimal: true,
              }}
              defaultValue={counterPrice === 0 ? '' : counterPrice?.toString()}
              placeholder="0.00"
              disabled={loadingSubmit}
              className="!text-[2rem]"
              onChange={(e) => {
                const newEvent = {
                  ...e,
                  target: {
                    ...e.target,
                    value:
                      e.target.value !== ''
                        ? parseFloat(e.target.value.replace(/[^0-9.]/g, ''))
                        : 0,
                  },
                };
                setCounterPrice(newEvent.target.value);
              }}
            />
          </div>
        </div>

        {taxError && (
          <div className="flex w-full justify-center">
            <span className="text-[1.4rem] font-semibold text-brand-red">
              {taxError.message}
            </span>
          </div>
        )}

        <div className="my-8 flex w-full justify-center">
          <button
            type="button"
            disabled={loadingSubmit || loadingTax || !!taxError}
            className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#C41719] p-6 text-[2rem] font-semibold text-white disabled:bg-brand-gray disabled:text-black"
            onClick={async () => {
              if (loadingSubmit || loadingTax) return;
              setLoadingSubmit(true);
              if (product) {
                try {
                  await counterOffer({
                    offer: {
                      ...offer,
                      price: counterPrice,
                      tax,
                    },
                    product,
                  });
                  onClose();
                } catch (e) {
                  logError(e);
                }
              }
              setLoadingSubmit(false);
            }}
          >
            {loadingSubmit
              ? 'Submitting...'
              : loadingTax
              ? 'Calculating tax...'
              : 'Submit'}
          </button>
        </div>
      </div>
    </>
  );
}

const DollarIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M10 1.875V18.125"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.375 6.875C14.375 6.46462 14.2942 6.05826 14.1371 5.67911C13.9801 5.29997 13.7499 4.95547 13.4597 4.66529C13.1695 4.37511 12.825 4.14492 12.4459 3.98788C12.0667 3.83083 11.6604 3.75 11.25 3.75H8.4375C7.6087 3.75 6.81384 4.07924 6.22779 4.66529C5.64174 5.25134 5.3125 6.0462 5.3125 6.875C5.3125 7.7038 5.64174 8.49866 6.22779 9.08471C6.81384 9.67076 7.6087 10 8.4375 10H11.875C12.7038 10 13.4987 10.3292 14.0847 10.9153C14.6708 11.5013 15 12.2962 15 13.125C15 13.9538 14.6708 14.7487 14.0847 15.3347C13.4987 15.9208 12.7038 16.25 11.875 16.25H8.125C7.2962 16.25 6.50134 15.9208 5.91529 15.3347C5.32924 14.7487 5 13.9538 5 13.125"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
